import {
  ElectricalConfiguration,
  ElectricalConfigurationInput,
  PhaseType,
  PhaseTypeLegacy,
  FrequencyStandard,
  VoltageLevel,
  CT_CLAMP_TYPES,
} from "../types/electricalConfig";

// Backend position configuration defaults for electrical fields
// These must match POSITION_CONFIGURATION_DEFAULT in odyssey/src/config/positionConfigurationDefault.ts
export const POSITION_CONFIGURATION_ELECTRICAL_DEFAULTS: ElectricalConfiguration = {
  voltage: 230,
  frequency: 50,
  powerFactor: 1.0,
  calibrationFactor: 1.10585258,
  nominalCurrent: 100,
  phaseType: "1P",
  sensorType: "r_coil",
  primary: "1000",
  secondary: "50",
};

// Get default electrical configuration - always returns backend defaults
// Position configurations are already defaulted based on country in the backend
export const getDefaultElectricalConfig = (): ElectricalConfiguration => {
  return { ...POSITION_CONFIGURATION_ELECTRICAL_DEFAULTS };
};

// Legacy function signature for backward compatibility
export const getRegionSpecificElectricalConfig = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _region: "NA" | "EU" | "ASIA" | "AU" = "NA",
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _phaseType: PhaseType = PhaseType.SINGLE_PHASE
): ElectricalConfiguration => {
  // Always return backend defaults since position configs are country-specific
  // TODO: Remove this deprecated function in next major version
  return getDefaultElectricalConfig();
};

// Get defaults that are compatible with backend position configuration
export const getBackendCompatibleElectricalDefaults = (): ElectricalConfiguration => {
  return { ...POSITION_CONFIGURATION_ELECTRICAL_DEFAULTS };
};

// Get electrical config defaults based on CT clamp type
export const getElectricalConfigForCTClamp = (
  ctClampValue: number,
  phaseType: PhaseType = PhaseType.SINGLE_PHASE,
  existingConfig: ElectricalConfiguration = {}
): ElectricalConfiguration => {
  const ctClampType = CT_CLAMP_TYPES.find((clamp) => clamp.value === ctClampValue);

  // Start with existing config or defaults, preserving user's voltage/frequency settings
  const baseConfig = Object.keys(existingConfig).length > 0 ? { ...existingConfig } : getDefaultElectricalConfig();

  if (ctClampType) {
    return {
      ...baseConfig,
      // Only update CT-specific fields, preserve voltage/frequency/other user settings
      nominalCurrent: ctClampType.maxCurrent,
      primary: ctClampType.maxCurrent.toString(),
      secondary: "5", // Standard secondary current for most CTs
      phaseType, // Use the specified phase type
    };
  }

  return {
    ...baseConfig,
    phaseType, // Use the specified phase type
  };
};

// Validate electrical configuration values
export const validateElectricalConfig = (
  config: ElectricalConfigurationInput
): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Voltage validation
  if (config.voltage !== undefined) {
    if (config.voltage < 100 || config.voltage > 1000) {
      errors.push("Voltage must be between 100V and 1000V");
    }
  }

  // Frequency validation
  if (config.frequency !== undefined) {
    if (![50, 60].includes(config.frequency)) {
      errors.push("Frequency must be either 50Hz or 60Hz");
    }
  }

  // Power factor validation
  if (config.powerFactor !== undefined) {
    if (config.powerFactor < 0 || config.powerFactor > 1) {
      errors.push("Power factor must be between 0.0 and 1.0");
    }
  }

  // Calibration factor validation
  if (config.calibrationFactor !== undefined) {
    if (config.calibrationFactor <= 0) {
      errors.push("Calibration factor must be greater than 0");
    }
  }

  // Nominal current validation
  if (config.nominalCurrent !== undefined) {
    if (config.nominalCurrent <= 0) {
      errors.push("Nominal current must be greater than 0");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Get CT clamp options for dropdowns
export const getCTClampOptions = () => {
  return CT_CLAMP_TYPES.map((clamp) => ({
    value: clamp.value,
    label: `${clamp.label} (${clamp.maxCurrent}A)`,
    maxCurrent: clamp.maxCurrent,
  }));
};

// Get voltage options - always use single-phase voltages since streams are measured individually
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getVoltageOptions = (_phaseType?: PhaseType | string) => {
  // Always return single-phase voltage options since we measure streams individually
  return [
    { value: VoltageLevel.V_110, label: "110V" },
    { value: VoltageLevel.V_120, label: "120V" },
    { value: VoltageLevel.V_220, label: "220V" },
    { value: VoltageLevel.V_230, label: "230V" },
    { value: VoltageLevel.V_240, label: "240V" },
  ];
};

// Get frequency options
export const getFrequencyOptions = () => [
  { value: FrequencyStandard.FREQ_50HZ, label: "50 Hz" },
  { value: FrequencyStandard.FREQ_60HZ, label: "60 Hz" },
];

// Get phase type options
export const getPhaseTypeOptions = () => [
  { value: PhaseType.SINGLE_PHASE, label: "Single Phase (1P)" },
  { value: PhaseType.THREE_PHASE, label: "Three Phase (3P)" },
];

// Get sensor type options - only show supported types
export const getSensorTypeOptions = () => [
  { value: "r_coil", label: "Rogowski Coil" },
  { value: "vct", label: "Voltage Controlled Transformer (VCT)" },
];

// Normalize sensor type from database value to supported values
export const normalizeSensorType = (sensorType: string | null | undefined): string | undefined => {
  if (!sensorType) return undefined;

  const normalized = sensorType.toLowerCase();

  // Map various sensor type values to our supported types
  if (normalized.includes("rogowski") || normalized === "r_coil") {
    return "r_coil";
  }

  if (normalized.includes("vct") || normalized.includes("voltage")) {
    return "vct";
  }

  // Default to r_coil for CT-like sensors
  if (normalized.includes("ct") || normalized.includes("current")) {
    return "r_coil";
  }

  return undefined;
};

// Normalize phase type from database value to supported values
export const normalizePhaseType = (phaseType: string | null | undefined): string | undefined => {
  if (!phaseType) return undefined;

  const normalized = phaseType.toUpperCase();

  // Handle current backend values
  if (normalized === "1P") return PhaseType.SINGLE_PHASE;
  if (normalized === "3P") return PhaseType.THREE_PHASE;

  // Handle legacy values for backward compatibility
  if (normalized.includes("SINGLE") || normalized === "1" || normalized === "SINGLE_PHASE") {
    return PhaseType.SINGLE_PHASE;
  }

  if (normalized.includes("THREE") || normalized === "3" || normalized === "THREE_PHASE") {
    return PhaseType.THREE_PHASE;
  }

  return undefined;
};

// Migration helper to convert legacy enum values to new ones
export const migratePhaseTypeValue = (legacyValue: string | undefined): string | undefined => {
  if (!legacyValue) return undefined;

  if (legacyValue === "single_phase" || legacyValue === PhaseTypeLegacy.SINGLE_PHASE) {
    return PhaseType.SINGLE_PHASE;
  }

  if (legacyValue === "three_phase" || legacyValue === PhaseTypeLegacy.THREE_PHASE) {
    return PhaseType.THREE_PHASE;
  }

  // If it's already the new format, return as is
  if (legacyValue === PhaseType.SINGLE_PHASE || legacyValue === PhaseType.THREE_PHASE) {
    return legacyValue;
  }

  return normalizePhaseType(legacyValue);
};

// Helper to convert phase type to display string
const formatPhaseTypeForDisplay = (phaseType: string): string => {
  switch (phaseType) {
    case PhaseType.SINGLE_PHASE:
    case "1P":
      return "single phase";
    case PhaseType.THREE_PHASE:
    case "3P":
      return "three phase";
    // Legacy values
    case "single_phase":
      return "single phase";
    case "three_phase":
      return "three phase";
    default:
      return phaseType.replace("_", " ");
  }
};

// Format electrical config for display
export const formatElectricalConfigForDisplay = (config: ElectricalConfiguration): string => {
  const parts: string[] = [];

  if (config.voltage) parts.push(`${config.voltage}V`);
  if (config.frequency) parts.push(`${config.frequency}Hz`);
  if (config.phaseType) parts.push(formatPhaseTypeForDisplay(config.phaseType));
  if (config.nominalCurrent) parts.push(`${config.nominalCurrent}A`);

  return parts.join(", ") || "Not configured";
};

// Check if electrical config is complete
export const isElectricalConfigComplete = (config: ElectricalConfiguration): boolean => {
  return !!(config.voltage && config.frequency && config.phaseType && config.nominalCurrent);
};

// Calculate power based on electrical parameters
export const calculatePower = (
  voltage: number,
  current: number,
  powerFactor: number = 0.85,
  phaseType: PhaseType = PhaseType.SINGLE_PHASE
): number => {
  if (phaseType === PhaseType.THREE_PHASE) {
    return Math.sqrt(3) * voltage * current * powerFactor;
  }
  return voltage * current * powerFactor;
};
